import { FC, useCallback, useMemo } from 'react';

import { useRouter } from 'next/router';
import Select, { SingleValue, StylesConfig } from 'react-select';

import { LanguageOption } from './locale-picker.types';
import { findOptionByValue, localeToDataMap } from './locale-picker.utils';
import { LocalePickerMenu } from './locale-picker-menu';
import { LocalePickerOption } from './locale-picker-option';
import { LocalePickerPlaceholder } from './locale-picker-placeholder';
import { LocalePickerSingleValue } from './locale-picker-single-value';
import { LocalePickerValueContainer } from './locale-picker-value-container';
import { LocalePickerIndicatorsContainer } from './localer-picker-indicators-container';

import { defaultLocale } from '@/constants/ui.constants';

const styles: StylesConfig<LanguageOption> = {
  menu: (styles) => ({
    ...styles,
    right: 0,
    width: 150
  })
};

export const LocalePicker: FC = () => {
  const router = useRouter();
  const currentLanguage = router.locale || router.defaultLocale || defaultLocale;

  // create options from language options
  const options = useMemo(() => {
    const locales = router.locales ?? [currentLanguage];
    return locales.map((locale) => ({
      label: localeToDataMap.get(locale)?.label ?? locale,
      value: locale,
      icon: localeToDataMap.get(locale)?.icon
    }));
  }, [currentLanguage, router.locales]);

  // implement handle on change and change next locale for current route
  const handleOnChange = useCallback(
    (newValue: SingleValue<LanguageOption>) => {
      const locale = newValue?.value;
      const { pathname, asPath, query } = router;
      router.push({ pathname, query }, asPath, { locale });
    },
    [router]
  );

  return (
    <Select<LanguageOption, false>
      options={options}
      instanceId="language-select"
      placeholder="Select a language..."
      defaultValue={findOptionByValue(options, currentLanguage)}
      onChange={handleOnChange}
      isSearchable={false}
      menuPosition="absolute"
      unstyled
      styles={styles}
      components={{
        Menu: LocalePickerMenu,
        IndicatorsContainer: LocalePickerIndicatorsContainer,
        ValueContainer: LocalePickerValueContainer,
        Placeholder: LocalePickerPlaceholder,
        Option: LocalePickerOption,
        SingleValue: LocalePickerSingleValue
      }}
    />
  );
};
